import {
  PANEL_OPERATION_METADATA,
  type CORE_OPERATION_METADATA,
  type CoreExactMutation,
  type CoreExactMutationResponseMap,
  type CoreQuery,
  type CoreRead,
  type CoreReadResponseMap,
  type PlatformRefusal,
  type IdentityResponsePOSTAuthLoginMemberships,
  type IdentityResponsePOSTAuthLoginMembershipsSelect,
} from '@exylon/generated-contract-kernel/types';
import { ACCOUNT_OPERATION_NAMES } from '../foundation/account-port';
import type { ConditionalRead, PanelReadOptions } from '../foundation/runtime-port';
import {
  contractSchemaMissing, contractValidator, loadContractValidator, installContractValidatorSource, preloadContractValidators,
  type ContractSchemaKind,
} from './contract-validators';

/** P-RT1 alone: screen operations and the unused staff passkey routes stay out. */
export const IDENTITY_RUNTIME_OPERATION_NAMES = Object.freeze({
  login: 'POST /auth/login',
  loginMemberships: 'POST /auth/login/memberships',
  selectLoginMembership: 'POST /auth/login/memberships/select',
  current: 'GET /auth/current',
  renew: 'POST /auth/renew',
  workspaces: 'GET /me/workspaces',
  // The runtime's one logout (row 5.4b, PS-08); Mi cuenta has no second path.
  logout: 'POST /auth/logout',
} as const satisfies Record<string, keyof typeof PANEL_OPERATION_METADATA>);

const allowedOperations = new Set<string>([
  ...Object.values(IDENTITY_RUNTIME_OPERATION_NAMES),
  ...Object.values(ACCOUNT_OPERATION_NAMES),
]);

export type IdentityHttpErrorKind =
  | 'network'
  | 'permission'
  | 'unauthenticated'
  | 'server'
  | 'invalid_response'
  | 'invalid_request'
  | 'canonical_refusal'
  /** Local and transient: a screen read waited for a foreground Identity
   * verification that has not finished. Nothing was refused by the server. */
  | 'session_verifying';

export class IdentityHttpError extends Error {
  constructor(
    readonly kind: IdentityHttpErrorKind,
    readonly status: number | null = null,
    readonly canonical: PlatformRefusal | null = null,
    /** A `network` failure caused by the transport's own deadline, not by the
     * connection: the request was aborted when its timeout elapsed. */
    readonly timedOut: boolean = false,
  ) {
    // Do not put credentials, tokens, request bodies or server copy in logs.
    super(`identity_http_${kind}`);
    this.name = 'IdentityHttpError';
  }
}

type CanonicalRefusalFinding = Readonly<{
  code: string;
  message: string;
  path?: string;
  target_kind?: 'floor' | 'table' | 'department';
  target_id?: string;
}>;

export type CanonicalRefusalDetail = Readonly<{
  code: string | null;
  message: string;
  findings: readonly CanonicalRefusalFinding[];
}>;

const refusalRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const canonicalRefusalEnvelope = (value: unknown): value is Readonly<Record<string, unknown>> =>
  refusalRecord(value)
  && Object.prototype.hasOwnProperty.call(value, 'statusCode')
  && Number.isInteger(value.statusCode)
  && (value.statusCode as number) >= 400
  && (value.statusCode as number) <= 599
  && Object.prototype.hasOwnProperty.call(value, 'message')
  && Object.prototype.hasOwnProperty.call(value, 'errors');

const refusalText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

function canonicalRefusalFinding(value: unknown): CanonicalRefusalFinding | null {
  if (!refusalRecord(value)) return null;
  const code = refusalText(value.code);
  if (code === null) return null;
  const message = refusalText(value.message) ?? code;
  const path = refusalText(value.path);
  const kind = value.target_kind, id = refusalText(value.target_id);
  return Object.freeze({ code, message, ...(path === null ? {} : { path }),
    ...((kind === 'floor' || kind === 'table' || kind === 'department') && id !== null ? { target_kind: kind, target_id: id } : {}) });
}

/**
 * Project display-safe detail only from the canonical envelope retained by the transport: `errors.errorCode` is the
 * one code, `errors.reason_text` the sentence the owner wrote for the staff, and the findings are the owner's.
 */
export function canonicalRefusalDetail(error: unknown): CanonicalRefusalDetail | null {
  const envelope = error instanceof IdentityHttpError ? error.canonical : error;
  if (!canonicalRefusalEnvelope(envelope)) return null;
  const errors = refusalRecord(envelope.errors) ? envelope.errors : null;
  const code = errors === null ? null : refusalText(errors.errorCode);
  const message = (errors === null ? null : refusalText(errors.reason_text)) ?? refusalText(envelope.message) ?? code;
  if (message === null) return null;
  const source = errors?.findings ?? (errors && Object.prototype.hasOwnProperty.call(errors, 'finding') ? [errors.finding] : []);
  const findings = Object.freeze((Array.isArray(source) ? source : [])
    .map(canonicalRefusalFinding)
    .filter((finding): finding is CanonicalRefusalFinding => finding !== null));
  return Object.freeze({ code, message, findings });
}

type GeneratedMetadata = (typeof PANEL_OPERATION_METADATA)[keyof typeof PANEL_OPERATION_METADATA];
/**
 * A media delivery names its bytes by digest and version and the owner answers it `immutable`: the browser may keep
 * it, so a photo is downloaded once and not on every visit. Every other read stays out of the browser's cache.
 */
const IMMUTABLE_READ_PATH = /\/media\/:mediaId\/delivery$/;
type Command = CoreRead | CoreExactMutation;

/** Requests are validated against the closed contract schemas; responses against the generated projections, which
 * permit additional response fields but keep all known fields, discriminator values and required properties strictly
 * validated. */
type ValidateSchema = (name: string, value: unknown) => boolean;
const validateWith = (kind: ContractSchemaKind): ValidateSchema => (name, value) => {
  const validate = contractValidator(kind, name);
  if (validate === undefined) throw new Error('identity_contract_schema_missing');
  return validate(value);
};
const validateRequest = validateWith('request');
const validateResponse = validateWith('response');

/** How an operation answers: JSON, or bytes (an image the contract types, or an owner's document). */
const responseKindOf = (metadata: GeneratedMetadata): string => 'response_kind' in metadata ? metadata.response_kind : 'json';

/** Every response schema an operation's answer may be validated against: its success, its declared refusals and the
 * platform refusal. */
function responseSchemas(metadata: GeneratedMetadata): readonly string[] {
  const refusals = 'refusal_responses' in metadata && Array.isArray(metadata.refusal_responses)
    ? (metadata.refusal_responses as readonly Readonly<{ schema: string }>[]).map(({ schema }) => schema) : [];
  const success = responseKindOf(metadata) === 'binary' ? [] : [metadata.response_schema];
  return [...new Set([...success, ...refusals, 'PlatformRefusal'])];
}

/**
 * Loads the validators that are not at hand yet; null when all are, so a warm request validates synchronously, with
 * the same microtask ordering as before any validator was loaded lazily. A validator that cannot load is a network
 * failure, retried by the next request; a schema the registry lacks stays the programming error it is.
 */
function validatorsReady(kind: ContractSchemaKind, names: readonly string[]): Promise<void> | null {
  const missing = names.filter((name) => contractValidator(kind, name) === undefined);
  if (missing.length === 0) return null;
  return Promise.all(missing.map((name) => loadContractValidator(kind, name))).then(() => undefined, (error: unknown) => {
    throw contractSchemaMissing(error) ? error : new IdentityHttpError('network');
  });
}

/**
 * Warms the validators of these operations: the request validators every mutation and filtered read waits for and,
 * unless `answers` is false, the answers' validators (the Identity reads at boot).
 */
export function preloadOperationValidators(operations: Iterable<string>, { answers = true }: Readonly<{ answers?: boolean }> = {}): void {
  const metadata = PANEL_OPERATION_METADATA as Readonly<Record<string, GeneratedMetadata | undefined>>;
  for (const operation of operations) {
    const entry = metadata[operation];
    if (entry === undefined) continue;
    if (entry.request_schema !== null) preloadContractValidators('request', [entry.request_schema]);
    if (answers) preloadContractValidators('response', responseSchemas(entry));
  }
}

/**
 * @internal Specs and tooling only: the whole generated registry compiled with Ajv, every schema synchronously at
 * hand once loaded. Installing it makes the transport validate with it too.
 */
export async function loadGeneratedValidators(): Promise<Readonly<{ request: ValidateSchema; response: ValidateSchema }>> {
  const compiled = await import('./contract-validators-compiled');
  await compiled.loadCompiledRegistry();
  installContractValidatorSource(compiled);
  return { request: validateRequest, response: validateResponse };
}

/** The operation's declared refusal for this status, validated against its generated shape. */
function declaredRefusal(
  metadata: GeneratedMetadata,
  status: number,
  value: unknown,
): PlatformRefusal | null {
  const declarations = 'refusal_responses' in metadata && Array.isArray(metadata.refusal_responses)
    ? metadata.refusal_responses as readonly Readonly<{ schema: string; status: number }>[] : [];
  for (const declaration of declarations) {
    if (declaration.status !== status) continue;
    if (validateResponse(declaration.schema, value)) return value as PlatformRefusal;
  }
  return null;
}

function metadataFor(command: Command, method: 'read' | 'mutate', operations: ReadonlySet<string>): GeneratedMetadata {
  const metadata = (PANEL_OPERATION_METADATA as Readonly<Record<string, GeneratedMetadata | undefined>>)[command.operation];
  if (!operations.has(command.operation) || !metadata || (metadata.method === 'GET') !== (method === 'read')) {
    throw new IdentityHttpError('invalid_request');
  }
  const keys = new Set(method === 'read'
    ? ['operation', 'params', 'query']
    : ['operation', 'params', 'query', 'body', 'idempotencyKey', 'expectedVersion', 'files']);
  if (Object.keys(command).some((key) => !keys.has(key)) || (method === 'mutate' && !('body' in command))) {
    throw new IdentityHttpError('invalid_request');
  }
  return metadata;
}

function pathFor(metadata: GeneratedMetadata, params: Command['params'], query: CoreQuery = {}): string {
  const names = [...metadata.path.matchAll(/:([A-Za-z][A-Za-z0-9_]*)/g)].map((match) => match[1]);
  if (params === null || typeof params !== 'object' || Array.isArray(params)
    || Object.keys(params).some((name) => !names.includes(name))) throw new IdentityHttpError('invalid_request');
  const path = metadata.path.replace(/:([A-Za-z][A-Za-z0-9_]*)/g, (_match, name: string) => {
    const value = (params as Readonly<Record<string, unknown>>)[name];
    if (typeof value !== 'string' || !value.trim()) throw new IdentityHttpError('invalid_request');
    return encodeURIComponent(value.trim());
  });
  if (query === null || typeof query !== 'object' || Array.isArray(query)) throw new IdentityHttpError('invalid_request');
  const values = new URLSearchParams();
  const append = (name: string, value: unknown) => {
    if (!['string', 'number', 'boolean'].includes(typeof value)
      || (typeof value === 'number' && !Number.isFinite(value))) throw new IdentityHttpError('invalid_request');
    values.append(name, String(value));
  };
  for (const [name, value] of Object.entries(query).sort(([left], [right]) => left.localeCompare(right))) {
    if (Array.isArray(value)) for (const item of value) append(name, item);
    else if (value !== null && value !== undefined) append(name, value);
  }
  const encoded = values.toString();
  return encoded ? `${path}?${encoded}` : path;
}

function mutationBody(metadata: GeneratedMetadata, command: CoreExactMutation, headers: Headers): BodyInit | undefined {
  if (metadata.request_schema !== null && !validateRequest(metadata.request_schema, command.body)) {
    throw new IdentityHttpError('invalid_request');
  }
  const expectedVersion = 'expectedVersion' in command ? command.expectedVersion : undefined;
  if (metadata.requires_expected_version && expectedVersion === undefined
    || expectedVersion !== undefined && (!metadata.requires_expected_version || !Number.isSafeInteger(expectedVersion) || expectedVersion < 0)) {
    throw new IdentityHttpError('invalid_request');
  }
  if (expectedVersion !== undefined) headers.set('If-Match', String(expectedVersion));
  const rawKey = command.idempotencyKey;
  const key = typeof rawKey === 'string' ? rawKey.trim() : '';
  if (rawKey !== undefined && (typeof rawKey !== 'string' || rawKey.length > 200 || /[\x00-\x1f\x7f]/.test(rawKey))
    || metadata.requires_idempotency_key && !key) throw new IdentityHttpError('invalid_request');
  if (key) headers.set('Idempotency-Key', key);

  const files = 'files' in command ? command.files : undefined;
  if (metadata.request_schema === null) {
    if (files !== undefined) throw new IdentityHttpError('invalid_request');
    return undefined;
  }
  if (metadata.request_media_type !== 'multipart/form-data') {
    if (files !== undefined) throw new IdentityHttpError('invalid_request');
    headers.set('Content-Type', 'application/json');
    return JSON.stringify(command.body);
  }
  if (files !== undefined && (files === null || typeof files !== 'object' || Array.isArray(files))) {
    throw new IdentityHttpError('invalid_request');
  }
  const form = new FormData();
  for (const [name, value] of Object.entries(command.body)) {
    if (value === undefined) continue;
    form.append(name, value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value));
  }
  const allowedFiles = new Set<string>(metadata.request_file_fields);
  for (const [name, file] of Object.entries(files ?? {})) {
    if (!allowedFiles.has(name) || !(file instanceof Blob)) {
      throw new IdentityHttpError('invalid_request');
    }
    form.append(name, file);
  }
  // The browser, not the adapter, supplies the multipart boundary.
  return form;
}

/** A strong entity tag: a quoted opaque string (RFC 9110 §8.8.3). */
const ENTITY_TAG = /^"[\x21\x23-\x7e]{1,200}"$/;
const NOT_MODIFIED: unique symbol = Symbol('not_modified');

export interface IdentityHttpClientOptions {
  readonly baseUrl: string;
  readonly fetch?: typeof fetch;
  readonly getToken: () => string | null;
  readonly timeoutMs?: number;
  /** A longer bound for named operations that outlive the default (the paid Catalog AI helpers). */
  readonly operationTimeoutsMs?: Readonly<Record<string, number>>;
  /** Explicit P-RT2 admission; the default client remains Identity/account only. */
  readonly additionalOperations?: readonly (keyof typeof PANEL_OPERATION_METADATA)[];
  /**
   * Named operations sent with `fetch(..., { keepalive: true })`, so a request made while the page is closing still
   * leaves (Menú's draft patches, docs/decisions/2026-10-31-menu-saves-in-the-background.md). A body over 64 KiB is
   * sent without it.
   */
  readonly keepaliveOperations?: readonly (keyof typeof PANEL_OPERATION_METADATA)[];
}

/** The browser refuses a `keepalive` request over 64 KiB: a larger body leaves as an ordinary request. */
const KEEPALIVE_MAX_BYTES = 60_000;
function keepaliveFits(body: BodyInit | undefined): boolean {
  return body === undefined || (typeof body === 'string' && new TextEncoder().encode(body).length <= KEEPALIVE_MAX_BYTES);
}

/** Generated transport only; session state and all retries belong to its owner. */
export class IdentityHttpClient {
  private readonly baseUrl: string;
  private readonly fetcher: typeof fetch;
  private readonly getToken: () => string | null;
  private readonly timeoutMs: number;
  private readonly operationTimeoutsMs: ReadonlyMap<string, number>;
  private readonly keepaliveOperations: ReadonlySet<string>;
  private readonly operations: ReadonlySet<string>;
  /** How far the server's clock is from this device's, from the `Date` of the latest fresh answer (0 before one). */
  private serverClockOffsetMs = 0;

  constructor(options: IdentityHttpClientOptions) {
    const base = options.baseUrl.trim().replace(/\/+$/, '');
    if (base.startsWith('/') && !base.startsWith('//')) {
      if (/[?#\\]/.test(base)) throw new IdentityHttpError('invalid_request');
    } else {
      let url: URL;
      try { url = new URL(base); } catch { throw new IdentityHttpError('invalid_request'); }
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
        throw new IdentityHttpError('invalid_request');
      }
    }
    this.baseUrl = base;
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.getToken = options.getToken;
    this.operations = new Set([...allowedOperations, ...options.additionalOperations ?? []]);
    this.timeoutMs = options.timeoutMs ?? 15_000;
    if (!Number.isFinite(this.timeoutMs) || this.timeoutMs <= 0) throw new IdentityHttpError('invalid_request');
    this.operationTimeoutsMs = new Map(Object.entries(options.operationTimeoutsMs ?? {}));
    this.keepaliveOperations = new Set(options.keepaliveOperations ?? []);
    for (const timeout of this.operationTimeoutsMs.values()) {
      if (!Number.isFinite(timeout) || timeout <= 0) throw new IdentityHttpError('invalid_request');
    }
  }

  /**
   * The server's clock as this device reads it. The server's instants (a session's end, for one) are judged on it: a
   * device whose clock runs ahead of the server's would otherwise see them early. Each fresh answer's `Date` sets it:
   * the header is cut to the second and stamped before the answer travels, so this never runs ahead of the server's
   * clock, and falls behind it by at most a second and the answer's way back.
   */
  serverNow = (): number => Date.now() + this.serverClockOffsetMs;

  /** A read may carry its owner's signal: the owner retiring it aborts the
   * fetch, beside the transport's own deadline. A mutation never takes one. */
  async read<C extends CoreRead>(command: C, options: PanelReadOptions = {}): Promise<CoreReadResponseMap[C['operation']]> {
    const metadata = metadataFor(command, 'read', this.operations);
    // A read with no request schema has nothing to validate before it leaves: it does not wait for a validator, and
    // its answer is validated before it is used, once the answer's validator is at hand.
    if (metadata.request_schema) {
      const ready = validatorsReady('request', [metadata.request_schema]);
      if (ready !== null) await ready;
      if (!validateRequest(metadata.request_schema, command.query ?? {})) throw new IdentityHttpError('invalid_request');
    }
    return await this.request(metadata, command, options.signal) as CoreReadResponseMap[C['operation']];
  }

  /**
   * A conditional read (docs/decisions/2026-10-07-pos-snapshot-revision-and-conditional-read.md),
   * only for an operation the contract declares `conditional_read: 'etag'`. With
   * `entityTag`, `If-None-Match` names it and a 304 answers `not_modified`; a 200
   * answers the validated value with the revision it carries.
   */
  async readConditional<C extends CoreRead>(command: C, entityTag: string | null, options: PanelReadOptions = {}): Promise<ConditionalRead<CoreReadResponseMap[C['operation']]>> {
    const metadata = metadataFor(command, 'read', this.operations);
    if ((metadata as Readonly<{ conditional_read?: string }>).conditional_read !== 'etag'
      || (entityTag !== null && !ENTITY_TAG.test(entityTag))) throw new IdentityHttpError('invalid_request');
    if (metadata.request_schema) {
      const ready = validatorsReady('request', [metadata.request_schema]);
      if (ready !== null) await ready;
      if (!validateRequest(metadata.request_schema, command.query ?? {})) throw new IdentityHttpError('invalid_request');
    }
    let received: string | null = null;
    const value = await this.request(metadata, command, options.signal, { ifNoneMatch: entityTag, entityTag: (tag) => { received = tag; } });
    if (value === NOT_MODIFIED) return Object.freeze({ kind: 'not_modified' as const });
    return Object.freeze({ kind: 'current' as const, value: value as CoreReadResponseMap[C['operation']], entityTag: received! });
  }

  async mutate<C extends CoreExactMutation>(command: C): Promise<CoreExactMutationResponseMap[C['operation']]> {
    return await this.request(metadataFor(command, 'mutate', this.operations), command) as CoreExactMutationResponseMap[C['operation']];
  }

  async readLoginMemberships(membershipSelectionProof: string): Promise<IdentityResponsePOSTAuthLoginMemberships> {
    return await this.mutate({ operation: IDENTITY_RUNTIME_OPERATION_NAMES.loginMemberships, params: {},
      body: { membershipSelectionProof } });
  }

  async selectLoginMembership(membershipSelectionProof: string, membershipId: string): Promise<IdentityResponsePOSTAuthLoginMembershipsSelect> {
    return await this.mutate({ operation: IDENTITY_RUNTIME_OPERATION_NAMES.selectLoginMembership, params: {},
      body: { membershipSelectionProof, membership_id: membershipId } });
  }

  private observeServerDate(date: string | null) {
    const at = date === null ? Number.NaN : Date.parse(date);
    if (Number.isFinite(at)) this.serverClockOffsetMs = at - Date.now();
  }

  private async request(metadata: GeneratedMetadata, command: Command, owner?: AbortSignal,
    conditional: Readonly<{ ifNoneMatch: string | null; entityTag: (tag: string) => void }> | null = null): Promise<unknown> {
    owner?.throwIfAborted();
    // The answer's validators load while the request travels; the answer awaits them before it is read.
    const answerReady = validatorsReady('response', responseSchemas(metadata));
    // Settled or not, the answer awaits it below; a failure here is not a second, unhandled rejection.
    answerReady?.catch(() => undefined);
    // A body is validated before it leaves.
    const bodySchema = 'body' in command ? metadata.request_schema : null;
    const bodyReady = bodySchema ? validatorsReady('request', [bodySchema]) : null;
    if (bodyReady !== null) await bodyReady;
    const url = this.baseUrl + pathFor(metadata, command.params, command.query);
    const headers = new Headers({ Accept: 'application/json', 'app-type': 'admin-panel' });
    if (!metadata.public) {
      const token = this.getToken()?.trim();
      if (!token) throw new IdentityHttpError('unauthenticated');
      if (/\s/.test(token)) throw new IdentityHttpError('invalid_request');
      headers.set('Authorization', `Bearer ${token}`);
    }
    const body = 'body' in command ? mutationBody(metadata, command, headers) : undefined;
    if (conditional?.ifNoneMatch) headers.set('If-None-Match', conditional.ifNoneMatch);
    const controller = new AbortController();
    // The controller is aborted only by this deadline, so an aborted signal is a timeout.
    const timer = globalThis.setTimeout(
      () => controller.abort(),
      this.operationTimeoutsMs.get(command.operation) ?? this.timeoutMs,
    );
    const signal = owner ? AbortSignal.any([controller.signal, owner]) : controller.signal;
    // The browser's cache may answer a media file, and revalidates a document by its digest; every other read is fresh.
    const cache: RequestCache = metadata.method === 'GET' && (IMMUTABLE_READ_PATH.test(metadata.path) || responseKindOf(metadata) === 'binary') ? 'default' : 'no-store';
    // An owner that retired the read receives its own reason, never a network failure.
    const failedTransport = (): unknown => owner?.aborted && !controller.signal.aborted ? owner.reason
      : new IdentityHttpError('network', null, null, controller.signal.aborted);
    try {
      let response: Response;
      try {
        response = await this.fetcher(url, {
          method: metadata.method,
          headers,
          ...(body === undefined ? {} : { body }),
          credentials: 'include',
          cache,
          redirect: 'error',
          signal,
          ...(this.keepaliveOperations.has(command.operation) && keepaliveFits(body) ? { keepalive: true } : {}),
        });
      } catch { throw failedTransport(); }
      // An answer the browser's cache gave carries the `Date` of when it was first sent: only a fresh one sets the clock.
      if (cache === 'no-store') this.observeServerDate(response.headers.get('Date'));

      // A conditional read: 304 only for the validator sent, and every answer names its revision.
      const entityTag = conditional === null ? null : response.headers.get('ETag');
      if (conditional !== null && response.status === 304) {
        if (conditional.ifNoneMatch === null || entityTag !== conditional.ifNoneMatch) throw new IdentityHttpError('invalid_response', 304);
        return NOT_MODIFIED;
      }
      const responseKind = responseKindOf(metadata);
      if (response.ok && (responseKind === 'binary-image' || responseKind === 'binary')) {
        if (!(metadata.response_statuses as readonly number[]).includes(response.status)) {
          throw new IdentityHttpError('invalid_response', response.status);
        }
        const mediaType = response.headers.get('Content-Type')?.split(';', 1)[0]?.trim().toLowerCase() ?? '';
        // An image names its types in the contract; a document's type is the owner's, and only has to be there.
        const declaredTypes = 'response_media_types' in metadata ? metadata.response_media_types as readonly string[] : null;
        if (mediaType === '' || (responseKind === 'binary-image' && !declaredTypes?.some((declared) => declared.toLowerCase() === mediaType))) {
          throw new IdentityHttpError('invalid_response', response.status);
        }
        if (conditional !== null) {
          if (entityTag === null || !ENTITY_TAG.test(entityTag)) throw new IdentityHttpError('invalid_response', response.status);
          conditional.entityTag(entityTag);
        }
        // A document sent as an attachment keeps its name: a `File` is the `Blob` every reader takes.
        const attachmentName = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1];
        try {
          const bytes = await response.arrayBuffer();
          return attachmentName === undefined ? new Blob([bytes], { type: mediaType }) : new File([bytes], attachmentName, { type: mediaType });
        }
        catch {
          if (signal.aborted) throw failedTransport();
          throw new IdentityHttpError('invalid_response', response.status);
        }
      }

      let value: unknown;
      try { value = await response.json(); } catch {
        if (signal.aborted) throw failedTransport();
        throw new IdentityHttpError(response.status === 401 ? 'unauthenticated'
          : response.status === 403 ? 'permission' : response.status >= 500 ? 'server' : 'invalid_response', response.status);
      }
      // A validator that cannot load is a network failure, as when it is awaited before the request.
      if (answerReady !== null) await answerReady;
      if (!response.ok) {
        // A declared owner refusal is more specific than the generic Identity
        // refusal. In particular, the generated Catalog 503 is a definite
        // custody refusal, while an undeclared 503 remains a server outage.
        const declared = declaredRefusal(metadata, response.status, value);
        const canonical = declared ?? (validateResponse('PlatformRefusal', value) ? value as PlatformRefusal : null);
        if (canonical !== null && canonical.statusCode !== response.status) throw new IdentityHttpError('invalid_response', response.status);
        // A coupon refusal validates as its declared generated shape or as the
        // platform refusal; the TPV port narrows its errorCode to the
        // already-admitted closed union.
        const kind: IdentityHttpErrorKind = response.status === 401 ? 'unauthenticated'
          : response.status === 403 ? 'permission' : declared !== null ? 'canonical_refusal'
            : response.status >= 500 ? 'server'
            : canonical !== null ? 'canonical_refusal' : 'invalid_response';
        throw new IdentityHttpError(kind, response.status, canonical);
      }
      if (!(metadata.response_statuses as readonly number[]).includes(response.status)
        || !validateResponse(metadata.response_schema, value)) throw new IdentityHttpError('invalid_response', response.status);
      if (conditional !== null) {
        if (entityTag === null || !ENTITY_TAG.test(entityTag)) throw new IdentityHttpError('invalid_response', response.status);
        conditional.entityTag(entityTag);
      }
      return value;
    } finally { globalThis.clearTimeout(timer); }
  }
}
